import { Typography, useEventCallback } from '@mui/material';
import { useFormikContext } from 'formik';
import { Fragment, memo, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { headCells } from './constants';
import { generatePath, useNavigate } from 'react-router-dom';
import { BodyResearchAmountVM, ResearchAmountVM } from 'front-office/view-models/research-amount';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

interface TableAmountProps {
  activeDelete?: boolean;
  activeEdit?: boolean;
  data?: ResearchAmountVM;
  onSubmitFilter: (values: ResearchAmountVM) => void;
}

const TableAmountComponent = ({
  activeDelete,
  activeEdit,
  data,
  onSubmitFilter,
}: TableAmountProps): React.ReactElement => {
  const formContext = useFormikContext<ResearchAmountVM>();

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<BodyResearchAmountVM>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<BodyResearchAmountVM>>({
    element: null,
    open: false,
  });

  const deleteData = useHttpClient(
    `${__CERTIFICATION_API_URL__}/certificazione/importo-pagamento`,
    { options: { method: 'DELETE' } }
  );

  const body: Array<BodyResearchAmountVM> = useMemo(() => {
    return data?.table?.list || [];
  }, [data]);

  const navigate = useNavigate();

  const onChangePage = useEventCallback((page: number) => {
    formContext.setFieldValue('table', {
      ...(formContext.values?.table || {}),
      page: page,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    formContext.setFieldValue('table', {
      ...(formContext.values?.table || {}),
      page: 0,
      rowsPerPage,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyResearchAmountVM): Promise<BodyResearchAmountVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/ricerca-importo/dettaglio/:codice', { codice: cell.code || '' })
        );
        resolve(cell);
      });
    }
  );

  const onEditPress = useEventCallback(
    (cell: BodyResearchAmountVM): Promise<BodyResearchAmountVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/ricerca-importo/modifica/:codice', { codice: cell.code || '' })
        );
        resolve(cell);
      });
    }
  );

  const onTrashPress = useEventCallback(
    (cell: BodyResearchAmountVM): Promise<BodyResearchAmountVM> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      });
    }
  );

  return (
    <Fragment>
      <div className="pmi-my-3">
        {body?.length > 0 ? (
          <Table
            ariaLabel="Lista lotti carte d'identità"
            page={formContext.values.table?.page || 0}
            rowsPerPage={formContext.values.table?.rowsPerPage}
            body={body as Array<BodyResearchAmountVM>}
            count={body?.length}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={onVisibilityPress}
            onTrashPress={onTrashPress}
            onEditPress={onEditPress}
            orders={{}}
            isNotLayoutFixed
            noSort
            activeActions={{
              delete: activeDelete,
              edit: activeEdit,
              read: true,
            }}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )}
      </div>
      <DeleteAlertModal
        title="Cancellazione Importo Pagamento"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteData({
              url: `/${deleteAlertModal.element?.code || ''}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Importo Pagamento"
        message={`La cancellazione del Importo Pagamento ${deleteSuccessModal.element?.code} è avvenuta con successo.`}
        onClose={() => {
          onSubmitFilter(formContext.values);
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          onSubmitFilter(formContext.values);
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

TableAmountComponent.displayName = 'Lots Management List - Table - Form';

const TableAmount = memo(TableAmountComponent);

export default TableAmount;
